import { useTotalFees } from '@melio/ap-domain';
import { Divider, Group, Text } from '@melio/penny';
import { BillingFeeSetting } from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import { FeeItemProps, forwardRef } from '@melio/platform-utils';

import { RecurringPaymentProps } from '../../types';
import { FeeItem } from './FeeItem';

export type FeesDetailsProps = {
  transactionFees: FeeItemProps[];
  recurringPayment?: RecurringPaymentProps;
  orgBillingFeeSettings: BillingFeeSetting[];
};

export const FeesDetails = forwardRef<FeesDetailsProps, 'div'>(
  ({ transactionFees, orgBillingFeeSettings, recurringPayment, ...props }, ref) => {
    const { formatMessage } = useMelioIntl();

    const { amount: totalFeesAmount, description: totalFeesDescription } = useTotalFees(
      transactionFees,
      orgBillingFeeSettings,
      recurringPayment?.lastAmountFees
    );

    return (
      <Group variant="vertical" spacing="m" {...props} ref={ref}>
        {transactionFees.length ? (
          <>
            <Group variant="vertical" spacing="s">
              {transactionFees.map((fee) => (
                <FeeItem key={fee.type} {...fee} />
              ))}
            </Group>
            <Divider />
          </>
        ) : null}
        <Group justifyContent="space-between">
          <Group variant="vertical" spacing="none">
            <Text as="p" textStyle="body1">
              {formatMessage('widgets.reviewAndConfirm.feesSection.totalFeesLabel')}
            </Text>
            <Text
              as="p"
              data-testid="review-and-confirm-total-fee-description"
              textStyle="body4"
              color="neutral.darkest"
            >
              {totalFeesDescription}
            </Text>
          </Group>
          <Text data-testid="review-and-confirm-total-fee-amount" textStyle="body1">
            {totalFeesAmount}
          </Text>
        </Group>
      </Group>
    );
  }
);

FeesDetails.displayName = 'FeesDetails';
